import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Star, ArrowUp, ArrowDown, Send, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full rounded-lg border border-sand-300 bg-white px-3 py-2 text-sm text-charcoal-800";
const EMPTY = { title: "", description: "", tag: "", thumbnail: "", embed_url: "", featured: false };

export default function VideosManager() {
  const [videos, setVideos] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const [tagFilter, setTagFilter] = useState("");

  const load = () => {
    api.get("/videos").then((r) => setVideos(r.data));
    api.get("/admin/video-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/videos/${form.id}`, form);
      else await api.post("/admin/videos", form);
      toast.success("Video saved.");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try { await api.delete(`/admin/videos/${v.id}`); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const ids = videos.map((v) => v.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.post("/admin/videos/reorder", { ids }); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const toggleFeatured = (v) => save({ ...v, featured: !v.featured });

  if (!videos) return <p className="text-charcoal-500">Loading…</p>;
  const tags = [...new Set(videos.map((v) => v.tag).filter(Boolean))];
  const shown = tagFilter ? videos.filter((v) => v.tag === tagFilter) : videos;
  const hasPetVideo = videos.some((v) => /pet/i.test(v.tag || ""));

  return (
    <div data-testid="admin-videos">
      <SectionTitle title="Life in Mexico — Videos" subtitle="Manage the video hub, feature videos, and send them personally to clients." />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="video-new-btn" onClick={() => setEditing({ ...EMPTY })}
            className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
            <Plus size={14} /> New video
          </button>
          {tags.length > 0 && (
            <select data-testid="video-tag-filter" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
              className="rounded-full border border-sand-300 bg-white px-3 py-2 text-sm text-charcoal-700">
              <option value="">All tags</option>
              {tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
        </div>
        <a href="/life-in-mexico" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-terracotta hover:underline">
          View public page <ExternalLink size={13} />
        </a>
      </div>
      {!hasPetVideo && (
        <div data-testid="pet-video-hint" className="mb-4 rounded-xl border border-terracotta/30 bg-terracotta-light px-4 py-3 text-sm text-terracotta-700">
          🐾 Tip: tag a video with <span className="font-medium">pets</span> and it will be sent automatically (once) to every client who tells us they're bringing pets.
        </div>
      )}

      <PetStoriesModeration />

      <div className="mb-4 rounded-xl border border-sand-200 bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-charcoal-600">🐾 Invite clients with pets to share an arrival clip (one friendly email each, never repeated).</p>
        <button data-testid="pet-story-invite-btn" onClick={async () => {
          try {
            const r = await api.post("/admin/pet-stories/invite");
            toast.success(r.data.invited ? `Invited ${r.data.invited} client(s)!` : "Everyone with pets has already been invited.");
          } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
        }} className="rounded-full bg-terracotta px-4 py-2 text-xs font-medium text-white hover:bg-terracotta-hover">
          Send story invitations
        </button>
      </div>

      {editing && <VideoForm form={editing} setForm={setEditing} onSave={save} onCancel={() => setEditing(null)} />}

      <div className="space-y-2">
        {shown.map((v, i) => (
          <Card key={v.id}>
            <div className="flex items-center gap-3" data-testid={`admin-video-${v.slug}`}>
              {v.thumbnail && <img src={v.thumbnail} alt="" className="h-12 w-20 rounded-lg object-cover shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-charcoal-800">{v.title}</span>
                  {v.featured && <Star size={13} className="shrink-0 fill-terracotta text-terracotta" />}
                </div>
                <div className="text-xs text-charcoal-500 capitalize">
                  {v.tag || "no tag"} · /life-in-mexico/{v.slug}
                  <span data-testid={`video-views-${v.slug}`} className="ml-2 normal-case text-charcoal-400">
                    · {analytics[v.slug]?.total_views || 0} view(s) · {analytics[v.slug]?.unique_viewers || 0} viewer(s)
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`video-up-${v.slug}`} onClick={() => move(i, -1)} className="p-1.5 text-charcoal-400 hover:text-terracotta"><ArrowUp size={14} /></button>
                <button data-testid={`video-down-${v.slug}`} onClick={() => move(i, 1)} className="p-1.5 text-charcoal-400 hover:text-terracotta"><ArrowDown size={14} /></button>
                <button data-testid={`video-feature-${v.slug}`} onClick={() => toggleFeatured(v)} title={v.featured ? "Unfeature" : "Feature"}
                  className={`p-1.5 ${v.featured ? "text-terracotta" : "text-charcoal-400 hover:text-terracotta"}`}><Star size={14} /></button>
                <button data-testid={`video-send-${v.slug}`} onClick={() => setSendFor(v)} className="p-1.5 text-charcoal-400 hover:text-sage" title="Send to a client"><Send size={14} /></button>
                <button data-testid={`video-edit-${v.slug}`} onClick={() => setEditing({ ...v })} className="p-1.5 text-charcoal-400 hover:text-terracotta"><Pencil size={14} /></button>
                <button data-testid={`video-delete-${v.slug}`} onClick={() => remove(v)} className="p-1.5 text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          </Card>
        ))}
        {videos.length === 0 && <Card><p className="text-sm text-charcoal-500">No videos yet — add your first one.</p></Card>}
      </div>

      {sendFor && <SendVideoModal video={sendFor} onClose={() => setSendFor(null)} />}
    </div>
  );
}

function PetStoriesModeration() {
  const [stories, setStories] = useState([]);
  const [preview, setPreview] = useState({});

  const load = () => api.get("/admin/pet-stories").then((r) => setStories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pending = stories.filter((s) => s.status === "pending");
  if (pending.length === 0) return null;

  const showPreview = async (s) => {
    if (s.kind === "link") return window.open(s.embed_url, "_blank");
    try {
      const res = await api.get(`/admin/pet-stories/${s.id}/media`, { responseType: "blob" });
      setPreview((p) => ({ ...p, [s.id]: URL.createObjectURL(res.data) }));
    } catch { toast.error("Could not load the clip."); }
  };

  const approve = async (s) => {
    try {
      const r = await api.post(`/admin/pet-stories/${s.id}/approve`, { featured: false });
      toast.success(`Live at /life-in-mexico/${r.data.video_slug} — client notified.`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const reject = async (s) => {
    const reason = window.prompt("Reason (optional, kept internal):", "") ?? null;
    if (reason === null) return;
    try { await api.post(`/admin/pet-stories/${s.id}/reject`, { reason }); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <Card className="mb-5" data-testid="pet-stories-moderation">
      <h3 className="font-medium text-charcoal-800">🐾 Client pet stories awaiting review ({pending.length})</h3>
      <p className="mt-1 text-xs text-charcoal-500">Approving publishes the clip to the video hub under "pet stories" and emails the client.</p>
      <div className="mt-3 space-y-3">
        {pending.map((s) => (
          <div key={s.id} data-testid={`pet-story-row-${s.id}`} className="rounded-xl border border-sand-200 bg-sand-100/60 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 text-sm">
                <span className="font-medium text-charcoal-800">{s.pet_name || "Pet"}</span>
                <span className="text-charcoal-500"> · from {s.client_name || s.client_email}</span>
                {s.caption && <p className="text-xs text-charcoal-600 mt-0.5">"{s.caption}"</p>}
              </div>
              <div className="flex shrink-0 gap-2">
                <button data-testid={`pet-story-preview-${s.id}`} onClick={() => showPreview(s)}
                  className="rounded-full border border-sand-300 px-3.5 py-1.5 text-xs font-medium text-charcoal-700 hover:border-terracotta">Preview</button>
                <button data-testid={`pet-story-approve-${s.id}`} onClick={() => approve(s)}
                  className="rounded-full bg-sage px-3.5 py-1.5 text-xs font-medium text-white hover:opacity-90">Approve & publish</button>
                <button data-testid={`pet-story-reject-${s.id}`} onClick={() => reject(s)}
                  className="rounded-full border border-sand-300 px-3.5 py-1.5 text-xs font-medium text-charcoal-500 hover:text-red-600">Reject</button>
              </div>
            </div>
            {preview[s.id] && (
              <video src={preview[s.id]} controls className="mt-3 max-h-64 w-full rounded-lg bg-black" data-testid={`pet-story-video-${s.id}`} />
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function VideoForm({ form, setForm, onSave, onCancel }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Card className="mb-5">
      <h3 className="font-medium text-charcoal-800 mb-3">{form.id ? "Edit video" : "New video"}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><input data-testid="video-form-title" className={FIELD} placeholder="Title *" value={form.title} onChange={set("title")} /></div>
        <div className="sm:col-span-2"><textarea data-testid="video-form-description" rows={2} className={FIELD} placeholder="Description" value={form.description} onChange={set("description")} /></div>
        <input data-testid="video-form-tag" className={FIELD} placeholder="Tag (e.g. neighborhoods, markets, pets)" value={form.tag} onChange={set("tag")} />
        <input data-testid="video-form-thumbnail" className={FIELD} placeholder="Thumbnail image URL" value={form.thumbnail} onChange={set("thumbnail")} />
        <div className="sm:col-span-2"><input data-testid="video-form-embed" className={FIELD} placeholder="Video URL (YouTube, Vimeo or direct file) *" value={form.embed_url} onChange={set("embed_url")} /></div>
        <label className="inline-flex items-center gap-2 text-sm text-charcoal-700">
          <input data-testid="video-form-featured" type="checkbox" className="accent-terracotta" checked={!!form.featured}
            onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} /> Featured (shown on homepage & portal)
        </label>
      </div>
      <div className="mt-4 flex gap-2">
        <button data-testid="video-form-save" onClick={() => form.title && form.embed_url ? onSave(form) : toast.error("Title and video URL are required.")}
          className="rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">Save</button>
        <button onClick={onCancel} className="rounded-full border border-sand-300 px-5 py-2 text-sm text-charcoal-700 hover:bg-sand-100">Cancel</button>
      </div>
    </Card>
  );
}

function SendVideoModal({ video, onClose }) {
  const [clients, setClients] = useState(null);
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => setClients([])); }, []);

  const send = async () => {
    if (!clientId) return toast.error("Pick a client.");
    setBusy(true);
    try {
      const res = await api.post(`/admin/videos/${video.id}/send`, { client_id: clientId });
      toast.success(`Sent to ${res.data.sent_to} — personalized with their name${clients.find((c) => c.id === clientId)?.pets?.length ? " and pet" : ""}.`);
      onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal-900/40 px-4" data-testid="send-video-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="font-medium text-charcoal-800">Send "{video.title}"</h3>
        <p className="mt-1 text-sm text-charcoal-500">The client gets a warm, personalized email with the video link (and their pet's name if we know it).</p>
        {!clients ? <p className="mt-4 text-sm text-charcoal-500">Loading clients…</p> : (
          <select data-testid="send-video-client" className={`${FIELD} mt-4`} value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Choose a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.preferred_name || c.name || c.email}{c.pets?.length ? ` (🐾 ${c.pets.map((p) => p.name).filter(Boolean).join(", ")})` : ""} — {c.email}
              </option>
            ))}
          </select>
        )}
        <div className="mt-5 flex gap-2">
          <button data-testid="send-video-confirm" onClick={send} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send email
          </button>
          <button onClick={onClose} className="rounded-full border border-sand-300 px-5 py-2 text-sm text-charcoal-700 hover:bg-sand-100">Cancel</button>
        </div>
      </div>
    </div>
  );
}
